import type { ApiErrorPayload } from "@/lib/types/api";

export class ApiError extends Error {
  status: number;
  payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

function isApiErrorPayload(value: unknown): value is ApiErrorPayload {
  return typeof value === "object" && value !== null && typeof (value as { error?: unknown }).error === "string";
}

/**
 * Ein deutscher Satz für alles, was aus `apiFetch` geflogen kommt — für die
 * Stellen, die ihn einer Person zeigen.
 *
 * Dieselbe Unterscheidung wie in der Sitzungsschicht und in der Warteschlange:
 * **ein `ApiError` ist eine Antwort des Servers** und trägt bereits eine
 * deutsche Meldung, die man zeigen soll. Alles andere ist gar keine Antwort —
 * ein `fetch`, der nie ankam. Dessen `message` gehört niemandem gezeigt: sie
 * ist unübersetzt und je Browser anders („Load failed" in Safari, „Failed to
 * fetch" in Chrome, „NetworkError…" in Firefox), und für die Person davor
 * bedeuten alle drei dasselbe.
 */
export function fehlertext(error: unknown, wobei: string): string {
  if (error instanceof ApiError) return error.message;
  return `${wobei} Prüfe deine Verbindung.`;
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  const isFormData = options.body instanceof FormData;
  if (!isFormData && !headers.has("Content-Type") && options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`/api${path}`, {
    ...options,
    headers,
    credentials: "same-origin"
  });

  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json") ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const message = isApiErrorPayload(payload) ? payload.error : `Serverfehler (${response.status})`;
    throw new ApiError(message, response.status, payload);
  }

  return payload as T;
}
